import React, {useLayoutEffect, useState, useEffect} from 'react';
import {
  SafeAreaView,
  View,
  useWindowDimensions,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import moment from 'moment';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  Calendar,
  Text,
  PrimaryButton,
  LinearProgress,
  CloseButton,
  Dialog,
} from '~/components/controls';
import {useDispatch, useSelector} from '~/lib/hooks';
import {addJobSetDate} from '~/actions';
import {PublicContentView} from '~/components/shared';
import {dealer as dealerApi} from '~/api/public';
import fontSizes from '~/styles/font-sizes';

import ShoppingCartIcon from '~/images/md-icons/shopping_cart/materialicons/24px.svg';

const weekdays = ['M', 'D', 'W', 'D', 'V', 'Z', 'Z'];

const SCROLLBAR_WIDTH = 17;
const MONTHS_IN_YEAR = 12;

const AddDate = ({navigation, route, onDismiss}) => {
  const dealer_id = route?.params?.dealer_id;
  const is_change = route?.params?.is_change;

  const dimensions = useWindowDimensions();
  const dispatch = useDispatch();
  const dealer = useSelector((state) => state.dealer);
  const date = useSelector((state) => state.date);
  const services = useSelector((state) => state.services);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState([]);
  const [showAlert, setShowAlert] = useState(false);

  const initialMonths = [
    ...Array(dealer?.availability_window?.months + 1 || MONTHS_IN_YEAR).keys(),
  ].map((value, index) => moment().startOf('month').add(index, 'month'));
  const months = useState(initialMonths)[0];

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={4} total={7} />
      ),
    });
  }, [navigation]);

  useEffect(() => {
    const fetch = async () => {
      const availability = await dealerApi.availability(
        dealer.id,
        services,
        dealer?.availability_window?.months || MONTHS_IN_YEAR,
      );
      setAvailability(availability);
      setLoading(false);
    };

    fetch();
  }, [dealer, services]);

  const onDateChange = (date) => {
    const time = moment(dealer.begin_checkin, 'HH:mm:ss');
    date.hours(time.hours());
    date.minutes(time.minutes());
    dispatch(addJobSetDate(date));
  };

  const onDateClear = () => {
    dispatch(addJobSetDate(null));
  };

  const onNext = async () => {
    if (is_change && navigation.canGoBack()) {
      navigation.goBack();
    } else if (onDismiss) {
      onDismiss();
    } else {
      navigation.navigate('Time', {dealer_id});
    }
  };

  const onShowAlert = async () => {
    setShowAlert(true);
  };

  const isValid = !!date;

  const width =
    (dimensions.width > 1024 ? 414 : dimensions.width) - SCROLLBAR_WIDTH;
  const cellWidth = Math.floor(width / 7);

  const content = (
    <View style={[sy.contentView]}>
      {!loading &&
        months.map((item) => (
          <Calendar
            key={`${moment(item).year()}-${moment(item).month()}`}
            cellWidth={cellWidth}
            month={item}
            date={date && new Date(date)}
            advance_notice={dealer.advance_notice}
            availability={availability}
            onPress={onDateChange}
            onShowAlert={onShowAlert}
            onClear={onDateClear}
          />
        ))}
    </View>
  );

  const footer = (
    <View style={sy.footerView}>
      <PrimaryButton
        disabled={!isValid}
        onPress={onNext}
        icon={
          is_change &&
          (() => <ShoppingCartIcon fill="#ffffff" width={18} height={18} />)
        }>
        {is_change ? 'Controleren' : onDismiss ? 'Opslaan' : 'Volgende'}
      </PrimaryButton>
    </View>
  );

  return (
    <SafeAreaView style={sy.mainView}>
      <View>
        {!onDismiss ? (
          <>
            <Divider />
            <View style={[sy['p-4']]}>
              <Text style={sy.xxLarge}>Datum afspraak</Text>
              <Text style={[sy.large, sy['mt-4']]}>
                Welke dag komt het uit dat je auto naar de werkplaats gaat?
              </Text>
            </View>
            <Divider />
          </>
        ) : (
          <View style={sy.modalHeaderView}>
            <CloseButton
              style={{position: 'absolute', top: 8, left: 8}}
              onPress={onDismiss}
            />
            <Text style={sy.medium}>Datum afspraak</Text>
          </View>
        )}
        <>
          {loading && (
            <View>
              <ActivityIndicator size="large" color="#231fda" />
            </View>
          )}
          {!loading && (
            <>
              <View style={[{height: cellWidth}]}>
                {weekdays.map((weekday, index) => (
                  <View
                    key={index}
                    style={[
                      {
                        position: 'absolute',
                        top: 0,
                        aspectRatio: 1,
                        left: index * cellWidth,
                        width: cellWidth,
                        height: cellWidth,
                      },
                    ]}>
                    <View
                      style={[
                        sy['flex-1'],
                        sy['items-center'],
                        sy['justify-center'],
                      ]}>
                      <Text
                        style={{
                          color: '#828282',
                          fontSize: 13,
                          fontFamily: 'Roboto_700Bold',
                        }}>
                        {weekday}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
              <Divider />
            </>
          )}
        </>
      </View>
      {onDismiss ? (
        <>
          <ScrollView>{content}</ScrollView>
          <Divider />
          <View>{footer}</View>
        </>
      ) : (
        <PublicContentView>
          {{
            content,
            footer,
          }}
        </PublicContentView>
      )}
      {showAlert && (
        <Dialog
          visible={true}
          onDismiss={() => setShowAlert(false)}
          buttons={[{text: 'OK', onPress: () => setShowAlert(false)}]}>
          <View style={[sy['gap-4'], sy['pb-4']]}>
            <Text>
              De gekozen datum is voor ons een drukke dag. We kunnen hierdoor
              geen plek garanderen.
            </Text>
            <Text style={fontSizes.mediumPlus}>
              Voor spoedreparaties en overige acute zaken hebben wij altijd
              plek.
            </Text>
            <Text>
              Neem contact met ons op door te bellen naar {dealer.phone_number}.
            </Text>
          </View>
        </Dialog>
      )}
    </SafeAreaView>
  );
};

export default AddDate;
